import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { KeyRound, ShieldCheck, User, Save, Lock } from 'lucide-react';

export function Settings({ showToast }) {
  const { user, updateProfile } = useAuth();

  const [name, setName] = useState(user?.name || 'TBS Administrator');
  const [email, setEmail] = useState(user?.email || 'admin@tbs-crm.com');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passError, setPassError] = useState('');

  // Handle Profile Details Update
  const handleUpdateProfile = (e) => {
    e.preventDefault();
    updateProfile({ name, email });
    if (showToast) showToast('Account profile updated successfully', 'success');
  };

  // Handle Password Change
  const handleChangePassword = (e) => {
    e.preventDefault();
    setPassError('');

    const expectedCurrentPass = user?.passwordHash || 'admin123';

    if (currentPassword !== expectedCurrentPass) {
      setPassError('Current password is incorrect.');
      return;
    }

    if (newPassword.length < 6) {
      setPassError('New password must be at least 6 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPassError('New password and confirmation do not match.');
      return;
    }

    updateProfile({ passwordHash: newPassword });
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');

    if (showToast) showToast('Password changed successfully!', 'success');
    alert(`Password updated successfully! Your new password is: ${newPassword}`);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '22px', maxWidth: '680px', margin: '0 auto', width: '100%', padding: '10px 0' }}>
      {/* Header with TBS Brand Accent Bar */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', gap: '4px', marginBottom: '8px' }}>
          <span style={{ width: 24, height: 4, borderRadius: '2px', backgroundColor: '#2cabe1' }}></span>
          <span style={{ width: 24, height: 4, borderRadius: '2px', backgroundColor: '#fdbb16' }}></span>
          <span style={{ width: 24, height: 4, borderRadius: '2px', backgroundColor: '#83c341' }}></span>
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#09090B' }}>Account Settings & Security</h2>

      </div>

      {/* Card 1: Account Profile Information */}
      <form onSubmit={handleUpdateProfile} className="card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', backgroundColor: '#FFFFFF' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: '#09090B', borderBottom: '1px solid #E4E4E7', paddingBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <User size={16} color="#2cabe1" />
          <span>Personal Profile Details</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#374151' }}>Full Name</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ width: '100%', height: '38px' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#374151' }}>Username (Login ID)</label>
            <input
              type="text"
              disabled
              value={user?.username || 'admin'}
              style={{ width: '100%', height: '38px', backgroundColor: '#F4F4F5', color: '#71717A' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#374151' }}>Email Address</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ width: '100%', height: '38px' }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
          <button
            type="submit"
            style={{
              height: '36px',
              padding: '0 16px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#2cabe1',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Save size={14} />
            <span>Save Profile</span>
          </button>
        </div>
      </form>

      {/* Card 2: Password Management */}
      <form onSubmit={handleChangePassword} className="card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', backgroundColor: '#FFFFFF' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: '#09090B', borderBottom: '1px solid #E4E4E7', paddingBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <KeyRound size={16} color="#83c341" />
          <span>Change Account Password</span>
        </div>

        {passError && (
          <div style={{ padding: '10px 14px', backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '6px', color: '#DC2626', fontSize: '12.5px' }}>
            {passError}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#374151' }}>Current Password</label>
          <input
            type="password"
            required
            placeholder="Enter current password (default: admin123)"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            style={{ width: '100%', height: '38px' }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#374151' }}>New Password</label>
            <input
              type="password"
              required
              placeholder="Min 6 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{ width: '100%', height: '38px' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#374151' }}>Confirm New Password</label>
            <input
              type="password"
              required
              placeholder="Repeat new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{ width: '100%', height: '38px' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
          <button
            type="submit"
            style={{
              height: '36px',
              padding: '0 18px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#090D16',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Lock size={14} />
            <span>Update Password</span>
          </button>
        </div>
      </form>

      {/* Card 3: Security Status with TBS Brand Colors */}
      <div className="card" style={{ padding: '16px 20px', backgroundColor: '#FAFAFC', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <ShieldCheck size={18} color="#83c341" />
          <div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#09090B' }}>Account Role: {user?.role || 'System Administrator'}</div>
            <div style={{ fontSize: '11.5px', color: '#71717A' }}>TBS Marketing Verified System • Session Active</div>
          </div>
        </div>
      </div>
    </div>
  );
}
